/**
 * L&G / Auralys — Theme JS
 * Minimaler Custom-Stack. Keine externen Dependencies.
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const moneyFormat = window.Shopify && Shopify.money_format || '€{{amount_with_comma_separator}}';

  // ═══ Utilities ═══
  function formatMoney(cents) {
    const value = (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '€' + value;
  }

  // ═══ Header scroll state ═══
  const header = document.querySelector('[data-header], #header');
  if (header) {
    let scrollTicking = false;
    const updateHeader = () => {
      const y = window.scrollY;
      header.dataset.scrolled = y > 8 ? 'true' : 'false';
      header.style.boxShadow = y > 20 ? '0 1px 0 rgba(0,0,0,0.04)' : 'none';
      scrollTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (!scrollTicking) {
        requestAnimationFrame(updateHeader);
        scrollTicking = true;
      }
    }, { passive: true });
    updateHeader();
  }

  // ═══ Inhaltsstoffe: force-open on desktop ═══
  const syncIngredientAccordion = () => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    document.querySelectorAll('[data-inhaltsstoffe-accordion] details').forEach((d, i) => {
      if (!isMobile) d.open = true;
      else if (i > 0) d.removeAttribute('open');
    });
  };
  syncIngredientAccordion();
  let igResizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(igResizeTimer);
    igResizeTimer = setTimeout(syncIngredientAccordion, 120);
  });

  // ═══ Mobile Nav Drawer ═══
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const mobileToggle = document.querySelector('[data-mobile-nav-toggle]');
  const mobileClose = document.querySelector('[data-mobile-nav-close]');
  const mobileBackdrop = document.querySelector('[data-mobile-nav-backdrop]');

  const openMobileNav = () => {
    if (!mobileNav) return;
    mobileNav.hidden = false;
    requestAnimationFrame(() => mobileNav.classList.add('is-open'));
    document.documentElement.style.overflow = 'hidden';
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');
  };
  const closeMobileNav = () => {
    if (!mobileNav) return;
    mobileNav.classList.remove('is-open');
    setTimeout(() => { mobileNav.hidden = true; }, 280);
    document.documentElement.style.overflow = '';
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
  };

  mobileToggle && mobileToggle.addEventListener('click', openMobileNav);
  mobileClose && mobileClose.addEventListener('click', closeMobileNav);
  mobileBackdrop && mobileBackdrop.addEventListener('click', closeMobileNav);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileNav && !mobileNav.hidden) closeMobileNav();
  });

  // ═══ Cart drawer ═══
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartClose = document.querySelector('[data-cart-close]');
  const cartBackdrop = document.querySelector('[data-cart-backdrop]');

  const openCart = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!cartDrawer) return;
    cartDrawer.hidden = false;
    requestAnimationFrame(() => cartDrawer.classList.add('is-open'));
    document.documentElement.style.overflow = 'hidden';
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    setTimeout(() => { cartDrawer.hidden = true; }, 260);
    document.documentElement.style.overflow = '';
  };

  document.querySelectorAll('[data-cart-toggle]').forEach(el => {
    el.addEventListener('click', openCart);
  });
  cartClose && cartClose.addEventListener('click', closeCart);
  cartBackdrop && cartBackdrop.addEventListener('click', closeCart);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer && !cartDrawer.hidden) closeCart();
  });

  // ═══ Cart-State-Renderer ═══
  function renderCartBody(cart) {
    const body = cartDrawer && cartDrawer.querySelector('[data-cart-body]');
    const footer = cartDrawer && cartDrawer.querySelector('[data-cart-footer]');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = '<div class="cart-drawer__empty-state">' +
        '<p class="cart-drawer__empty">Dein Warenkorb ist leer.</p>' +
        '<a class="btn btn--primary" href="/products/auralys-for-youth-face-elixier" data-cart-close>Zum Elixir</a>' +
        '</div>';
      if (footer) footer.hidden = true;
    } else {
      let html = '<ul class="cart-drawer__lines">';
      cart.items.forEach(item => {
        const img = item.image ? `<img src="${item.image}" alt="${item.product_title.replace(/"/g,'&quot;')}" width="80" height="100" loading="lazy">` : '';
        html += `<li class="cart-drawer__line" data-line-key="${item.key}">
          <div class="cart-drawer__line-media">${img}</div>
          <div class="cart-drawer__line-info">
            <a class="cart-drawer__line-title" href="${item.url}">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-drawer__line-variant">${item.variant_title}</div>` : ''}
            <div class="cart-drawer__line-controls">
              <div class="cart-drawer__qty" role="group" aria-label="Menge">
                <button type="button" class="cart-drawer__qty-btn" data-qty-change="${item.key}" data-qty-delta="-1" aria-label="Weniger">−</button>
                <span class="cart-drawer__qty-val" data-qty-val>${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-qty-change="${item.key}" data-qty-delta="1" aria-label="Mehr">+</button>
              </div>
              <button type="button" class="cart-drawer__line-remove" data-qty-change="${item.key}" data-qty-set="0" aria-label="Entfernen">Entfernen</button>
            </div>
          </div>
          <div class="cart-drawer__line-price">${formatMoney(item.final_line_price)}</div>
        </li>`;
      });
      html += '</ul>';
      body.innerHTML = html;

      if (footer) {
        footer.hidden = false;
        const subtotalEl = footer.querySelector('[data-cart-subtotal]');
        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      }
    }

    // Update counts everywhere
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.classList.toggle('is-hidden', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-count-sr]').forEach(el => {
      el.textContent = cart.item_count + ' Artikel im Warenkorb';
    });

    // Sticky-Cart
    const stickyCart = document.querySelector('[data-sticky-cart]');
    if (stickyCart) {
      stickyCart.hidden = cart.item_count === 0;
      const stickyCount = stickyCart.querySelector('[data-sticky-cart-count]');
      const stickyPrice = stickyCart.querySelector('[data-sticky-cart-price]');
      if (stickyCount) stickyCount.textContent = cart.item_count;
      if (stickyPrice) stickyPrice.textContent = formatMoney(cart.total_price);
    }

    // Re-attach close buttons in empty state
    body.querySelectorAll('[data-cart-close]').forEach(el => {
      if (el.tagName !== 'BUTTON' && el.tagName !== 'A') return;
      el.addEventListener('click', (e) => {
        if (el.tagName === 'BUTTON') { e.preventDefault(); closeCart(); }
      });
    });

    // Attach qty-change handlers
    body.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', handleQtyChange);
    });
  }

  function handleQtyChange(e) {
    const btn = e.currentTarget;
    const lineKey = btn.getAttribute('data-qty-change');
    const delta = parseInt(btn.getAttribute('data-qty-delta') || '0', 10);
    const absoluteSet = btn.getAttribute('data-qty-set');
    const lineEl = btn.closest('[data-line-key]');
    let newQty;
    if (absoluteSet !== null) {
      newQty = parseInt(absoluteSet, 10);
    } else {
      const curQty = parseInt(lineEl.querySelector('[data-qty-val]').textContent, 10) || 1;
      newQty = curQty + delta;
    }
    if (newQty < 0) newQty = 0;
    updateCartLine(lineKey, newQty);
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  async function updateCartLine(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      const cart = await res.json();
      renderCartBody(cart);
    } catch (err) { console.warn('cart/change failed', err); }
  }

  async function addToCart(formData, cta) {
    try {
      if (cta) cta.classList.add('is-loading');
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      if (!res.ok) throw new Error('add failed');
      const cart = await fetchCart();
      renderCartBody(cart);
      openCart();
    } catch (err) {
      console.warn('cart/add failed', err);
      // Fallback: hard submit
      if (cta && cta.form) cta.form.submit();
    } finally {
      if (cta) cta.classList.remove('is-loading');
    }
  }

  // ═══ PDP Add-to-Cart Intercept ═══
  document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const cta = form.querySelector('[data-atc], [name="add"]');
      addToCart(new FormData(form), cta);
    });
  });

  // ═══ Initial render (on load) ═══
  if (cartDrawer) {
    fetchCart().then(renderCartBody).catch(() => {});
  }

  if (reducedMotion) return;

  // ═══ Hero-Parallax ═══
  const hero = document.querySelector('.hero-cosmic');
  if (hero) {
    const heroBg = hero.querySelector('.hero-bg, .hero-bg-img');
    const sparkles = hero.querySelectorAll('.sparkle');
    let pTicking = false;
    const updateParallax = () => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        const scrollY = Math.max(0, -rect.top);
        if (heroBg) heroBg.style.transform = `translate3d(0, ${scrollY * 0.15}px, 0)`;
        sparkles.forEach((s, i) => {
          const factor = 0.2 + (i % 5) * 0.05;
          s.style.setProperty('--parallax', `${scrollY * factor}px`);
        });
      }
      pTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (!pTicking) {
        requestAnimationFrame(updateParallax);
        pTicking = true;
      }
    }, { passive: true });
  }

  // ═══ Sacred-Geometry-Reveal auf Pillars ═══
  const pillars = document.querySelectorAll('.pillar');
  if (pillars.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('pillar--revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    pillars.forEach((p, i) => {
      p.style.setProperty('--reveal-delay', `${i * 180}ms`);
      observer.observe(p);
    });
  }
})();
